import { debugMessage } from './debug.js';
import { defineAdSlot, getDefinedAdSlot } from './define.js';
import { loadSlotElement } from './dom.js';
import { initRefreshState } from './refresh/state.js';
import { requestBids } from './request.js';

/**
 * Find and return ad slot node(s), based on the dfp-ad class.
 */
const findAdSlotNodes = (node) => {
  const adClass = 'dfp-ad';

  if (!node.tagName) {
    return []; // not an element
  }

  if (node.classList.contains(adClass)) {
    return [node];
  }

  // Multiple nodes found
  if (node.firstElementChild) {
    return [...node.getElementsByClassName(adClass)];
  }

  return [];
};

/**
 * Process any ad slot elements that were added to the DOM.
 */
const processNewNodes = (nodes) => {
  const dynamicSlots = nodes.map((slotElement) => {
    debugMessage('Dynamic ad slot detected', { slotElement });
    const slotDetails = loadSlotElement(slotElement);
    const slot = (
      getDefinedAdSlot(slotDetails.id)
      || defineAdSlot(slotDetails)
    );

    return slot;
  }).filter((x) => x);

  // Render the new ad slots
  if (dynamicSlots.length) {
    initRefreshState(dynamicSlots);
    requestBids(dynamicSlots);
  }
};

/**
 * Process any ad slot elements that were removed from the DOM.
 */
const processOldNodes = (nodes) => {
  nodes.forEach((slotElement) => {
    const slot = getDefinedAdSlot(slotElement.id);

    if (slot) {
      debugMessage('Destroying ad slot that was removed from the DOM', { slot });

      googletag.destroySlots([slot]);
    }
  });
};

/**
 * Watch for any ad slots that are added dynamically.
 *
 * For example, those we slot into content loaded in the hub.
 */
export const watchForDynamicAdSlots = () => {
  const mutationCallback = (mutationsList) => {
    const newNodes = new Set();
    const oldNodes = new Set();

    // Use traditional 'for loops' for IE 11
    // eslint-disable-next-line no-restricted-syntax
    for (const { addedNodes, removedNodes } of mutationsList) {
      // eslint-disable-next-line no-restricted-syntax
      for (const node of addedNodes) {
        findAdSlotNodes(node).forEach((newNode) => {
          newNodes.add(newNode);
        });
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const node of removedNodes) {
        findAdSlotNodes(node).forEach((oldNode) => {
          oldNodes.add(oldNode);
        });
      }
    }

    processOldNodes([...oldNodes]);
    processNewNodes([...newNodes]);
  };

  const observer = new MutationObserver(mutationCallback);
  const container = document.body;
  const config = {
    attributes: true,
    childList: true,
    subtree: true,
  };

  observer.observe(container, config);
};
